import React, {useEffect, useCallback, useState} from "react";
import {
  Col,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import {FaAngleRight} from "react-icons/fa";
import {useDispatch, useSelector} from "react-redux";
import {useCleanupOnUnload} from "../../hooks/useCleanupOnUnload";
import {
  createMetricAlert,
  requestMetricCreateData,
  requestMetricField,
  requestMetricGraph,
  storeMetricField,
} from "../../redux/Actions/alerts.actions";
import {storeDataExplorerAnalysis} from "../../redux/Actions/data-explorer.actions";

import {
  getMetricCreateData,
  getMetricFieldList,
} from "../../redux/Selectors/alerts.selector";
import {getDataExplorerAnalysis} from "../../redux/Selectors/data-explorer.selectors";
import {useScopeAndAppliances} from "../../hooks/useScopeAndAppliances";
import {useFetchAlerts} from "../../views/Alerts/alertsTable/useAlertTableData";
import AsteriskIcon from "../asteriskIcon";
import ZeusButton from "../Button";
import Cells from "../Charts/Containers/Cell/Cells";
import Loader from "../Loader";
import AlertFormLabel from "./components/AlertFormLabel";
import SelectCreator from "./components/SelectCreator";
import TreeSelector from "./components/TreeSelector";
import ToggleCreator from "./components/ToggleCreator";
import "./forms.scss";

const AddMetricForm = (props) => {
  const {closeAlertForm, data, showAlertForm} = props;
  const dispatch = useDispatch();
  const {refetch} = useFetchAlerts();
  const metricFieldOptions = useSelector(getMetricFieldList);
  const analysis = useSelector(getDataExplorerAnalysis);
  const metricCreateOptions = useSelector(getMetricCreateData);
  const [formData, setFormData] = useState(data || {});
  const {
    scopeOptions: siteScopeOptions,
    applianceOptions: controllerList,
    fetchScope,
    fetchAppliances,
  } = useScopeAndAppliances();

  const {
    alarm_levels = [],
    time_unit_list = [],
    metric_type: metric_types = [],
    threshold_types = [],
  } = metricCreateOptions;

  const {
    name,
    description,
    level,
    inventory,
    every_type,
    every_value,
    re_type,
    re_value,
    metric_type,
    metric_field,
    threshold_type,
    value,
    min_value,
    max_value,
    enabled = true,
    scope = [],
    sms_enabled = true,
  } = formData;

  const cleanup = useCallback(() => {
    dispatch(storeMetricField());
    dispatch(storeDataExplorerAnalysis({}));
  }, [dispatch]);

  useCleanupOnUnload(cleanup);

  const getMetricGraph = (data) => {
    const {
      inventory,
      level,
      metric_type,
      metric_field,
      threshold_type,
      max_value,
      min_value,
      value,
    } = data;
    const payload = {mac: inventory, metric_type, metric_field};
    if (
      (threshold_type === "is_above" || threshold_type === "is_below") &&
      value &&
      level
    ) {
      payload.level = level;
      payload.threshold_type = threshold_type;
      payload.value = value;
    } else if (
      (threshold_type === "is_inside_range" || threshold_type === "is_outside_range") &&
      min_value &&
      max_value &&
      level
    ) {
      payload.level = level;
      payload.threshold_type = threshold_type;
      payload.min_value = min_value;
      payload.max_value = max_value;
    }

    if (inventory && metric_field && metric_type) {
      dispatch(requestMetricGraph(payload));
    } else {
      dispatch(storeDataExplorerAnalysis({}));
    }
  };

  const getMetricField = (inventory, metricType) => {
    if (metricType && inventory) {
      dispatch(
        requestMetricField({
          mac: inventory,
          metric_type: metricType,
        })
      );
    } else {
      dispatch(storeMetricField());
    }
  };

  useEffect(() => {
    fetchScope();
    dispatch(requestMetricCreateData());
    fetchAppliances(scope);
    getMetricField(inventory, metric_type);
    getMetricGraph(formData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChange = (e, type, name) => {
    let update_modal_data = {...formData};
    //select
    if (type === 1) {
      update_modal_data = Object.assign({}, update_modal_data, {
        [name]: e?.target?.value,
      });
    }

    // number or text
    if (type === 2) {
      if (e.target.type === "number") {
        update_modal_data = Object.assign({}, update_modal_data, {
          [name]: parseFloat(e.target.value),
        });
      } else {
        update_modal_data = Object.assign({}, update_modal_data, {
          [name]: e.target.value,
        });
      }
    }

    //toggle
    if (type === 3) {
      update_modal_data = Object.assign({}, update_modal_data, {[name]: e});
    }

    //removing re_value if re_type is cleared
    if (name === "re_type") {
      if (!e.target.value) {
        // eslint-disable-next-line no-unused-vars
        const {re_type, ...rest} = update_modal_data;
        update_modal_data = rest;
        update_modal_data = Object.assign({}, update_modal_data, {
          ["re_value"]: "",
        });
      }
    }
    //deleting fields on change of metric_type
    if (name === "metric_type") {
      update_modal_data.metric_field = null;

      if (e?.target?.value === "sensor_alarm") {
        update_modal_data = Object.assign({}, update_modal_data, {
          ["threshold_type"]: "is_above",
          ["value"]: 0,
        });

        // eslint-disable-next-line no-unused-vars
        const {min_value, max_value, ...rest} = update_modal_data;
        update_modal_data = rest;
      } else {
        update_modal_data.threshold_type = null;
        // eslint-disable-next-line no-unused-vars
        const {value, min_value, max_value, ...rest} = update_modal_data;
        update_modal_data = rest;
      }
    }
    setFormData(update_modal_data);

    const keysToGetMetricField = ["inventory", "metric_type"];
    if (keysToGetMetricField.includes(name)) {
      getMetricField(update_modal_data.inventory, update_modal_data.metric_type);
    }

    const graphPayloadKeys = [
      "inventory",
      "level",
      "metric_type",
      "metric_field",
      "threshold_type",
      "max_value",
      "min_value",
      "value",
    ];
    if (graphPayloadKeys.includes(name)) {
      getMetricGraph(update_modal_data);
    }
  };

  const handleScopeChange = (value) => {
    const callback = (newOptions) => {
      const isSelectedInventoryAvailable = newOptions.some(
        (item) => item.value === inventory
      );

      if (!isSelectedInventoryAvailable) {
        setFormData((prevData) => ({
          ...prevData,
          inventory: null,
          metric_field: null,
        }));

        getMetricField(null, null);
        getMetricGraph({});
      }
    };

    setFormData((prevData) => ({
      ...prevData,
      scope: value,
    }));

    fetchAppliances(value, callback);
  };

  const handleSubmit = () => {
    let sendingData = {
      name: name,
      description: description,
      level: level,
      scope,
      mac: inventory,
      metric_type: metric_type,
      metric_field: metric_field,
      threshold_type: threshold_type,
      enabled: enabled,
      every_type: every_type,
      every_value: every_value,
      re_type: re_type,
      re_value: re_value,
      sms_enabled,
    };
    if (threshold_type === "is_above" || threshold_type === "is_below") {
      sendingData = {...sendingData, value: value};
    } else {
      sendingData = {
        ...sendingData,
        min_value: min_value,
        max_value: max_value,
      };
    }
    if (!re_value) {
      // eslint-disable-next-line no-unused-vars
      const {re_value, re_type, ...rest} = sendingData;
      sendingData = rest;
    }
    if (!re_type) {
      // eslint-disable-next-line no-unused-vars
      const {re_type, re_value, ...rest} = sendingData;
      sendingData = rest;
    }

    const callback = () => {
      closeAlertForm();
      refetch();
    };

    dispatch(createMetricAlert(sendingData, formData.id, callback));
  };

  return (
    <Modal
      unmountOnClose={true}
      isOpen={showAlertForm}
      backdrop={"static"}
      className="custom-container"
      contentClassName="custom-width"
    >
      <Loader />
      <ModalHeader className="alert_modal_header" toggle={closeAlertForm}>
        <Col xs="12">
          <div style={{display: "flex", alignItems: "start"}}>
            <AsteriskIcon />
            <Input
              type="text"
              name="name"
              placeholder={"Enter Alert Name"}
              className="modal_edit_header br-none roboto-bold"
              value={name}
              onChange={(e) => handleOnChange(e, 2, "name")}
              data-cy="alert_Name"
            />
          </div>
        </Col>
        <div className="desc--type--container" style={{color: "#525f7f"}}>
          <Col sm="12" xl="5">
            <div style={{display: "flex", alignItems: "start"}}>
              <AsteriskIcon />
              <Input
                type="text"
                name="description"
                placeholder={"Enter Alert Description"}
                className="br-none modal_edit_desc"
                value={description}
                onChange={(e) => handleOnChange(e, 2, "description")}
                data-cy="alert_Desp"
              />
            </div>
          </Col>
          <Col sm="12" md="6" lg="4" xl="3">
            <div style={{display: "flex"}}>
              <div className="mt--3">
                <AsteriskIcon />
              </div>{" "}
              <SelectCreator
                closeMenuOnSelect={true}
                options={alarm_levels}
                onChange={(e) => {
                  handleOnChange(e, 1, "level");
                }}
                value={level}
                className={"alert-select-creator"}
                colSize={12}
                placeholder={"Select Type"}
                id={"alarmLevels"}
                classNamePrefix={"alarmLevels"}
              />
            </div>
          </Col>
        </div>
      </ModalHeader>
      <ModalBody>
        <Row>
          {/* Form section */}
          <Col sm="12" xl="5">
            <div className="main-text-container">
              <FaAngleRight /> <span className="ml-1 main-text roboto-bold">DEFINE</span>
            </div>
            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Site(s)" />
                  </Col>
                  <Col md="9">
                    <TreeSelector
                      defaultValue={scope}
                      options={siteScopeOptions}
                      onChange={handleScopeChange}
                      childrenKey={"locations"}
                      id={"scope"}
                      classNamePrefix={"scope"}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={{size: 9, offset: 3}}>
                    <p className="alert-info-text roboto-normal">Select one or more</p>
                  </Col>
                </Row>
              </FormGroup>
            </Col>

            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Appliance" />
                  </Col>
                  <SelectCreator
                    closeMenuOnSelect={true}
                    options={controllerList}
                    value={inventory}
                    onChange={(e) => {
                      handleOnChange(e, 1, "inventory");
                    }}
                    className={"alert-select-creator"}
                    colSize={9}
                    placeholder={"Select Appliance"}
                    id={"controllerList"}
                    classNamePrefix={"controllerList"}
                  />
                </Row>
              </FormGroup>
            </Col>

            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Metric" />
                  </Col>
                  <SelectCreator
                    closeMenuOnSelect={true}
                    options={metric_types}
                    onChange={(e) => {
                      handleOnChange(e, 1, "metric_type");
                    }}
                    value={metric_type}
                    className={"alert-select-creator padding-top-mobile"}
                    colSize={9}
                    placeholder={"Metric type"}
                    id={"metricType"}
                    classNamePrefix={"metricType"}
                  />
                </Row>
              </FormGroup>
            </Col>
            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <SelectCreator
                    closeMenuOnSelect={true}
                    options={metricFieldOptions}
                    value={metric_field}
                    onChange={(e) => {
                      handleOnChange(e, 2, "metric_field");
                    }}
                    className={"alert-select-creator"}
                    colSize={{size: 9, offset: 3}}
                    placeholder={"Metric Field"}
                    id={"metricField"}
                    classNamePrefix={"metricField"}
                  />
                </Row>
              </FormGroup>
            </Col>
            {metric_type !== "sensor_alarm" && (
              <Col sm="12" md="12" xs="12" lg="12">
                <FormGroup className="custom-margin-bottom">
                  <Row>
                    <Col md="3">
                      <AlertFormLabel label="Threshold" />
                    </Col>

                    <SelectCreator
                      closeMenuOnSelect={true}
                      options={threshold_types}
                      onChange={(e) => {
                        handleOnChange(e, 1, "threshold_type");
                      }}
                      value={threshold_type}
                      className={"alert-select-creator padding-top-mobile"}
                      colSize={9}
                      placeholder="Threshold type"
                      id={"ThresholdType"}
                      classNamePrefix={"ThresholdType"}
                    />
                  </Row>

                  {(threshold_type === "is_above" || threshold_type === "is_below") && (
                    <Row className="padding-top-alert-row">
                      <Col md="3">
                        <AlertFormLabel label="Value" />
                      </Col>
                      <Col md="9">
                        <Input
                          type="text"
                          value={value}
                          name="value"
                          onChange={(e) => handleOnChange(e, 2, "value")}
                          placeholder="enter value"
                          className="input-number-height"
                          data-cy="thresholdValue"
                        />
                      </Col>
                    </Row>
                  )}
                  {(threshold_type === "is_inside_range" ||
                    threshold_type === "is_outside_range") && (
                    <Row className="padding-top-alert-row">
                      <Col md="3">
                        <AlertFormLabel label="Value" />
                      </Col>
                      <Col md="9">
                        <Row>
                          <Col md="6">
                            <Input
                              type="text"
                              value={min_value}
                              name="min_value"
                              onChange={(e) => handleOnChange(e, 2, "min_value")}
                              placeholder="minimum value"
                              className="input-number-height"
                            />
                          </Col>
                          <Col md="6" className="padding-top-mobile">
                            <Input
                              type="text"
                              value={max_value}
                              name="max_value"
                              onChange={(e) => handleOnChange(e, 2, "max_value")}
                              placeholder="maximum value"
                              className="input-number-height"
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  )}
                </FormGroup>
              </Col>
            )}

            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3" className="check--every--label">
                    <AlertFormLabel label="Check Every" />
                  </Col>
                  <Col md="9">
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          value={every_value}
                          name="every_value"
                          onChange={(e) => handleOnChange(e, 2, "every_value")}
                          placeholder="Enter value"
                          className="input-number-height"
                          data-cy="CheckEveryValue"
                        />
                      </Col>
                      <SelectCreator
                        closeMenuOnSelect={true}
                        options={time_unit_list}
                        onChange={(e) => {
                          handleOnChange(e, 1, "every_type");
                        }}
                        value={every_type}
                        className={"alert-select-creator padding-top-mobile"}
                        colSize={6}
                        placeholder={"Select"}
                        id={"CheckEveryUnit"}
                        classNamePrefix={"CheckEveryUnit"}
                      />
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col md={{size: 9, offset: 3}}>
                    <p className="alert-info-text check-every-text roboto-normal">
                      Frequency to check for alarm status
                    </p>
                  </Col>
                </Row>
              </FormGroup>
            </Col>

            {/* Notify Section*/}
            <div className="main-text-container">
              <FaAngleRight />
              <span className="ml-1 main-text roboto-bold">RE-NOTIFY</span>
            </div>

            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Alerts" required={false} />
                  </Col>
                  <Col md="9">
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          value={re_value}
                          name="re_value"
                          onChange={(e) => handleOnChange(e, 2, "re_value")}
                          placeholder="Enter value"
                          className="input-number-height"
                        />
                      </Col>

                      <SelectCreator
                        closeMenuOnSelect={true}
                        options={time_unit_list}
                        onChange={(e) => {
                          handleOnChange(e, 1, "re_type");
                        }}
                        value={re_type}
                        className={"alert-select-creator padding-top-mobile"}
                        colSize={6}
                        placeholder={"Select"}
                        isClearable={true}
                      />
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col md={{size: 9, offset: 3}}>
                    <p className="alert-info-text roboto-normal">
                      If alert event is unresolved
                    </p>
                  </Col>
                </Row>
              </FormGroup>
            </Col>
            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="SMS Notifications" required={false} />
                  </Col>
                  <Col md="9">
                    <ToggleCreator
                      large={true}
                      testId="trigger"
                      onChange={() => {
                        handleOnChange(!sms_enabled, 3, "sms_enabled");
                      }}
                      checked={sms_enabled}
                    />
                  </Col>
                </Row>
              </FormGroup>
            </Col>
          </Col>
          {/* Graph section */}
          <Col sm="12" xl="7">
            <div className="main-text-container">
              <FaAngleRight />
              <span className="ml-1 main-text roboto-bold" data-cy={"preview"}>
                PREVIEW
              </span>
            </div>

            {analysis && (
              <div>
                <Cells
                  dashboardId={"metric"}
                  cells={[analysis]}
                  isDraggable={false}
                  isResizable={false}
                />
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <ZeusButton
          type="default"
          withShadow
          withRadius
          onClick={handleSubmit}
          data-cy="submit-btn"
        >
          {formData.id ? "UPDATE" : "CREATE"}
        </ZeusButton>
        <ZeusButton
          type="info-small"
          withShadow
          withRadius
          onClick={closeAlertForm}
          data-cy="cancel-btn"
        >
          CANCEL
        </ZeusButton>
      </ModalFooter>
    </Modal>
  );
};

AddMetricForm.propTypes = {};

export default AddMetricForm;
